/**
 * Quiz CTA Generator
 * 
 * Centralized utility for generating US Citrus Nursery quiz-promoting CTAs.
 * Used across RewriteBlogPostActionModal, PAAContentActionModal, and BlogContentActionModal.
 * 
 * All CTA content is generated client-side (no API call needed).
 * CTAs link to https://uscitrusnursery.com/pages/quiz with UTM tracking.
 */

// Popular tree images from Shopify CDN for the rotating animation
const TREE_IMAGES = [
  {
    url: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260108_123357.jpg?v=1768229478',
    name: 'Meyer Lemon',
  },
  {
    url: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/new_rio_red.jpg?v=1770327311',
    name: 'Rio Red Grapefruit',
  },
  {
    url: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260113_113417.jpg?v=1768407174',
    name: 'Australian Finger Lime',
  },
  {
    url: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260202_141722.jpg?v=1770327166',
    name: 'Pink Variegated Lemon',
  },
  {
    url: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260108_112934.jpg?v=1768246530',
    name: 'Key Lime',
  },
  {
    url: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260128_145205.jpg?v=1769711056',
    name: 'Eureka Lemon',
  },
];

export { TREE_IMAGES };

// ===== CTA VARIANT TYPES =====

export interface QuizCtaVariant {
  cta: {
    category_header: string;
    headline: string;
    description: string;
    action_button: string;
  };
  shortcode: string;
}

export interface GeneratedQuizCTAs {
  cta_variants: {
    beginning: QuizCtaVariant;
    middle: QuizCtaVariant;
    end: QuizCtaVariant;
  };
}

// ===== CTA COPY POOLS =====
// Multiple options per position so users can cycle through them

const BEGINNING_VARIANTS: QuizCtaVariant['cta'][] = [
  {
    category_header: 'FIND YOUR PERFECT TREE',
    headline: 'Which Citrus Tree Was Made For You?',
    description: 'Take our 60-second quiz and discover the perfect citrus tree for your climate, taste, and growing style. Over 10,000 citrus lovers have found their match.',
    action_button: 'Take the Quiz',
  },
  {
    category_header: 'CITRUS MATCH QUIZ',
    headline: 'Not Sure Which Tree to Start With?',
    description: "Answer 11 quick questions and we'll match you with the citrus trees that actually thrive in your zone. It takes less than a minute.",
    action_button: 'Find My Match',
  },
  {
    category_header: 'PERSONALIZED PICKS',
    headline: 'Your Perfect Citrus Tree is Waiting',
    description: "Stop guessing. Our quiz matches you with citrus varieties based on your climate, experience level, and what you want to grow. Free and takes 60 seconds.",
    action_button: 'Start the Quiz',
  },
];

const MIDDLE_VARIANTS: QuizCtaVariant['cta'][] = [
  {
    category_header: 'STILL BROWSING?',
    headline: 'Still Reading? Your Perfect Tree is Waiting',
    description: 'Over 10,000 citrus lovers found their ideal tree with our 60-second quiz. Answer a few questions and get personalized recommendations for your exact climate and taste.',
    action_button: 'Find My Match',
  },
  {
    category_header: 'QUICK QUIZ',
    headline: "Curious Which Trees Grow Best Where You Live?",
    description: "Our quiz factors in your zip code, growing experience, and flavor preferences to recommend trees that will actually thrive in your yard or patio.",
    action_button: 'Take the Quiz',
  },
  {
    category_header: 'TREE FINDER',
    headline: "Want to Know Which Citrus Tree Fits Your Life?",
    description: 'Lemon lover? Grapefruit fan? Not sure yet? Our 60-second quiz narrows 40+ varieties down to your perfect matches based on your lifestyle.',
    action_button: 'Get My Results',
  },
];

const END_VARIANTS: QuizCtaVariant['cta'][] = [
  {
    category_header: 'READY TO GROW?',
    headline: "Ready to Grow? Let's Find Your Tree",
    description: "Answer 11 quick questions and we'll show you the exact citrus trees that match your climate, growing experience, and taste preferences. Free shipping on orders $99+.",
    action_button: 'Start the Quiz',
  },
  {
    category_header: 'YOUR CITRUS JOURNEY',
    headline: "Find the Citrus Trees That Were Made for You",
    description: "Take our 60-second quiz — we'll match you with trees that thrive in your zone, fit your experience level, and taste exactly how you like. Thousands have found their perfect tree.",
    action_button: 'Take the Quiz Now',
  },
  {
    category_header: 'GET MATCHED',
    headline: "Don't Leave Without Finding Your Perfect Tree",
    description: "Our citrus quiz has helped 10,000+ growers discover their ideal trees. It takes 60 seconds, it's free, and you'll get personalized picks shipped right to your door.",
    action_button: 'Find My Trees',
  },
];

// Button text options for cycling
export const QUIZ_BUTTON_OPTIONS = [
  'Take the Quiz',
  'Find My Match',
  'Start the Quiz',
  'Get My Results',
  'Find My Trees',
  'Take the Quiz Now',
  'Discover My Tree',
  'Get Matched',
];

// ===== CORE FUNCTIONS =====

/**
 * Generate quiz CTA variants for all three positions.
 * Client-side only — no API call needed.
 */
export function generateQuizCTAs(variantIndex = 0): GeneratedQuizCTAs {
  const bIdx = variantIndex % BEGINNING_VARIANTS.length;
  const mIdx = variantIndex % MIDDLE_VARIANTS.length;
  const eIdx = variantIndex % END_VARIANTS.length;

  return {
    cta_variants: {
      beginning: {
        cta: { ...BEGINNING_VARIANTS[bIdx] },
        shortcode: `[QUIZ_CTA_BEGINNING]`,
      },
      middle: {
        cta: { ...MIDDLE_VARIANTS[mIdx] },
        shortcode: `[QUIZ_CTA_MIDDLE]`,
      },
      end: {
        cta: { ...END_VARIANTS[eIdx] },
        shortcode: `[QUIZ_CTA_END]`,
      },
    },
  };
}

/**
 * Build UTM-tracked quiz URL following the pattern:
 * https://uscitrusnursery.com/pages/quiz?utm_campaign=blog_creator_{slug}_{position}cta
 */
export function generateQuizUtmUrl(keyword: string, position: string): string {
  const slug = keyword
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 80);

  const params = new URLSearchParams({
    utm_source: 'us_citrus_blog',
    utm_medium: 'content_marketing',
    utm_campaign: `blog_creator_${slug}_${position}cta`,
  });

  return `https://uscitrusnursery.com/pages/quiz?${params.toString()}`;
}

/**
 * Generate the full inline-styled HTML for a quiz CTA.
 * Uses uscitrusnursery.com brand colors: green gradient, cream text, yellow button.
 * Includes a rotating tree image carousel via CSS animation.
 */
/**
 * Returns the CSS keyframes needed for the CTA image rotation.
 * Callers should inject this once per page/article, not per CTA.
 */
export function getQuizCtaStyles(): string {
  const count = TREE_IMAGES.slice(0, 4).length;
  return `<style>
    @keyframes quizCtaFade${count} {
      0% { opacity: 0; }
      5% { opacity: 1; }
      ${Math.round(100 / count)}% { opacity: 1; }
      ${Math.round(100 / count) + 5}% { opacity: 0; }
      100% { opacity: 0; }
    }
  </style>`;
}

export function getQuizCtaHtml(
  ctaData: QuizCtaVariant,
  quizUrl: string
): string {
  const trees = TREE_IMAGES.slice(0, 4);

  const imageFrames = trees
    .map(
      (tree, i) =>
        `<img src="${tree.url}" alt="${tree.name}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain; border-radius: 12px; animation: quizCtaFade${trees.length} ${trees.length * 3}s infinite; animation-delay: ${i * 3}s; opacity: 0;" />`
    )
    .join('\n      ');

  return `<div style="background: #4E960C; padding: 2rem; border-radius: 1rem; margin: 2rem 0; position: relative; overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
  <div style="display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap;">
    <div style="flex: 1; min-width: 240px;">
      <div style="font-size: 0.75rem; font-weight: 700; color: #FFD93D; margin-bottom: 0.5rem; letter-spacing: 0.15em; text-transform: uppercase;">
        ${ctaData.cta.category_header}
      </div>
      <h4 style="font-size: 1.5rem; font-weight: 800; color: #FFFAEE; margin: 0 0 0.75rem 0; line-height: 1.3;">
        ${ctaData.cta.headline}
      </h4>
      <p style="font-size: 1rem; color: #FFFAEE; line-height: 1.6; margin: 0 0 1.25rem 0; opacity: 0.92;">
        ${ctaData.cta.description}
      </p>
      <a href="${quizUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-flex; align-items: center; gap: 0.5rem; padding: 0.875rem 1.75rem; background-color: #FFD93D; color: #1a1a1a; border-radius: 0.625rem; font-size: 1.05rem; font-weight: 700; cursor: pointer; text-decoration: none; transition: background-color 0.2s ease; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
        🍋 ${ctaData.cta.action_button}
        <span style="font-size: 1.1rem;">→</span>
      </a>
    </div>
    <div style="width: 200px; height: 240px; position: relative; flex-shrink: 0;">
      ${imageFrames}
    </div>
  </div>
</div>`;
}

/**
 * Count quiz CTAs in content using comment markers.
 */
export function countQuizCTAs(content: string): number {
  if (!content) return 0;
  const matches = content.match(/<!-- Quiz CTA:/g);
  return matches ? matches.length : 0;
}

/**
 * Remove all quiz CTAs from content using comment markers.
 * Falls back to gradient identifier if markers missing.
 */
export function clearQuizCTAs(content: string): string {
  let cleaned = content;

  // Primary: remove by comment markers
  cleaned = cleaned.replace(
    /<!-- Quiz CTA:.*?-->[\s\S]*?<!-- End Quiz CTA -->/g,
    ''
  );

  // Fallback: remove by unique background color identifier
  // The CTA has 4 levels of nesting: outer > flex > text div + image div
  cleaned = cleaned.replace(
    /<div style="background: #4E960C[^>]*>[\s\S]*?<\/div>\s*<\/div>\s*<\/div>\s*<\/div>/g,
    ''
  );

  // Collapse excessive newlines
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();

  return cleaned;
}
